import { useState } from "react";
import { TableCell, TableSortLabel } from "@mui/material";
import MDTypography from "../../controls/MDTypography";

// Valores y formatos que comparten la pantalla del Informe de Gestión, el detalle de cada gestor y las ventanas de la tabla de posiciones.

export const MODALIDADES = ["Voluntario", "Obligatorio", "Mixto"];

// Valores a partir de los cuales una cifra se marca como señal a revisar. Son indicadores, no sanciones: sirven para mirar con
// más atención a quien los supera.
export const PORCENTAJE_NO_VUELVEN_ALTO = 50;
export const TIEMPO_MUERTO_ALTO_MIN = 30;

// Los mismos umbrales que usa el backend (CalculadorInformeDisparador): una pausa es «inmediata» si empieza antes de estos minutos
// desde que se asignó el caso, y una resolución es «rápida» si lleva menos de estos minutos de trabajo efectivo.
export const PAUSA_INMEDIATA_MIN = 2;
export const RESOLUCION_RAPIDA_MIN = 1;

export const minutosLegibles = (min) => {
  if (min === null || min === undefined) return "-";
  const total = Math.round(min);
  if (total < 60) return `${total} min`;
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, "0")} min`;
};

export const numero = (valor) => (valor ?? 0).toLocaleString("es-AR");

// Orden de una tabla por el título de sus columnas. Un clic ordena de menor a mayor, otro clic invierte el orden y un tercero
// vuelve al orden original (el mismo ciclo que las demás tablas del sistema). `clave` identifica la columna ordenada.
export const useOrdenTabla = () => {
  const [orden, setOrden] = useState({ clave: null, descendente: false });
  const alternar = (clave) =>
    setOrden((actual) => {
      if (actual.clave !== clave) return { clave, descendente: false };
      return actual.descendente ? { clave: null, descendente: false } : { clave, descendente: true };
    });
  return [orden, alternar];
};

// Devuelve las filas ordenadas por la columna elegida. `valores` dice cómo sacar de una fila el valor de cada columna ordenable
// ({ clave: (fila) => valor }); los números se comparan como números y los textos sin distinguir mayúsculas ni tildes. Las filas
// sin valor (null o vacío) quedan siempre al final, en cualquiera de los dos sentidos, y las que empatan conservan su orden.
export const ordenarFilas = (filas, orden, valores) => {
  const valorDe = valores[orden.clave];
  if (!valorDe) return filas;
  const signo = orden.descendente ? -1 : 1;
  const vacio = (v) => v === null || v === undefined || v === "" || Number.isNaN(v);
  return [...filas].sort((a, b) => {
    const x = valorDe(a);
    const y = valorDe(b);
    if (vacio(x) || vacio(y)) return vacio(x) === vacio(y) ? 0 : vacio(x) ? 1 : -1;
    const diferencia = typeof x === "string" || typeof y === "string" ? String(x).localeCompare(String(y), "es", { sensitivity: "base", numeric: true }) : x - y;
    return diferencia * signo;
  });
};

// Título de una columna. Con `clave` se puede tocar para ordenar la tabla por esa columna (`tabla` es el [orden, alternar] de
// useOrdenTabla); sin `clave` es un título común. `resaltada` lo pinta de color para marcar la columna por la que viene ordenado el listado.
export function CabeceraOrdenable({ texto, tabla, clave, ancho, resaltada = false }) {
  const [orden, alternar] = tabla;
  const ordenada = Boolean(clave) && orden.clave === clave;
  const titulo = (
    <MDTypography variant="caption" fontWeight="bold" color={resaltada ? "info" : "text"}>
      {texto}
    </MDTypography>
  );
  return (
    <TableCell sx={ancho ? { width: ancho } : undefined} sortDirection={ordenada ? (orden.descendente ? "desc" : "asc") : false}>
      {clave ? (
        <TableSortLabel active={ordenada} direction={ordenada && orden.descendente ? "desc" : "asc"} onClick={() => alternar(clave)}>
          {titulo}
        </TableSortLabel>
      ) : (
        titulo
      )}
    </TableCell>
  );
}
